/* @flow */

import * as React from 'react';
import {useSelector} from 'react-redux';

import {getAvatarUrl} from 'nutshell-core/url/get-avatar-url';
import type {DisplayOptionEntity} from 'nutshell-core/utils';
import {formValueSelector, type FieldProps} from 'redux-form';

import {Avatar, IconAvatar} from 'shells/avatar';
import {colors} from 'shells/colors';
import {TrashIcon, ContactIcon} from 'shells/icon';
import {StackLegacy} from 'shells/layout';
import {EntityPageAddItemButton} from 'shells/lead-sidebar';
import {
    ContactCardCreateable,
    type ContactInput,
    ContactCard,
} from 'shells/peep-cards/contact-card';

import type {ContactCreateValidationInput} from 'nutshell-graphql-types';

import {LEADS_FORM_KEY} from '../../../../../entity-form/forms/lead-form-connected';
import {useGetContactFormValidation} from '../../../../graphql/hooks';

import {contactInputToDisplayOptionEntity} from './helpers';
import {getPotentialContacts} from './rich-contact-select-helpers';

const leadFormSelector = formValueSelector(LEADS_FORM_KEY);

const NEW_CONTACT_MAGIC_ID = 'new-contact-magic-id';

type Props = {
    ...FieldProps,
};

export function RichContactSelect(props: Props): React.Node {
    const {input} = props;

    const [isEditingContactId, setIsEditingContactId] = React.useState<?string>(null);
    const [validatedAvatarUrls, setValidatedAvatarUrls] = React.useState<{[key: string]: string}>(
        {}
    );

    const alreadyAddedContactsIds = React.useMemo(
        () => (Array.isArray(input.value) ? input.value.map((c) => c.id) : []),
        [input.value]
    );

    const {refetch: fetchFormValidation} = useGetContactFormValidation();

    const selectedAccounts = useSelector((state) => leadFormSelector(state, 'links.accounts'));
    const selectedAccountIds =
        selectedAccounts &&
        selectedAccounts.filter((account) => !account.isNew).map((account) => account.id);

    const handleCreateContact = (contactInput: ContactInput) => {
        input.onChange([
            ...input.value,
            contactInputToDisplayOptionEntity(
                contactInput,
                true,
                String(Date.now()) + String(Math.random())
            ),
        ]);

        setIsEditingContactId(null);
    };

    const handleUpdateContact = (contactInput: ContactInput) => {
        const updatedContact = contactInputToDisplayOptionEntity(contactInput, true);

        input.onChange(
            input.value.map((contact) =>
                contact.id === isEditingContactId ? {id: contact.id, ...updatedContact} : contact
            )
        );
        setIsEditingContactId(null);
    };

    const handleSelectContact = (contact: DisplayOptionEntity) => {
        input.onChange([...input.value, contact]);
        setIsEditingContactId(null);
    };

    const handleRemoveContact = (id: ?string) => {
        input.onChange(input.value.filter((contact) => contact.id !== id));
        setIsEditingContactId(null);
    };

    React.useEffect(() => {
        if (Array.isArray(input.value)) {
            const urlsToFetch = input.value
                .filter((contact) => contact.avatarUrl && !validatedAvatarUrls[contact.id])
                .map((contact) => ({id: contact.id, url: contact.avatarUrl}));

            if (urlsToFetch.length > 0) {
                Promise.all(
                    urlsToFetch.map((item) =>
                        getAvatarUrl(item.url).then((url) => ({id: item.id, url}))
                    )
                ).then((results) => {
                    setValidatedAvatarUrls((prev) => {
                        const newUrls = {...prev};
                        results.forEach((result) => {
                            newUrls[result.id] = result.url;
                        });

                        return newUrls;
                    });
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input.value]);

    return (
        <StackLegacy spacing={12}>
            {input.value &&
                input.value.map((contact) => {
                    const avatarUrlValidated = validatedAvatarUrls[contact.id];

                    if (contact.id === isEditingContactId) {
                        return (
                            <ContactCardCreateable
                                key={contact.id}
                                onSelectContact={handleSelectContact}
                                onSubmit={handleUpdateContact}
                                onClose={() => setIsEditingContactId(null)}
                                validate={(contactInput: ContactCreateValidationInput) =>
                                    fetchFormValidation(contactInput)
                                }
                                temporaryContact={{
                                    id: contact.id,
                                    name: contact.value,
                                    description: contact.entityDescription,
                                    phoneNumber: contact.primaryPhoneText,
                                    email: contact.primaryEmailText,
                                    jobTitle: contact.jobTitle,
                                }}
                                hasRelatedAccounts={false}
                            />
                        );
                    }

                    return (
                        <ContactCard
                            key={contact.id}
                            name={contact.value}
                            description={contact.entityDescription}
                            phoneNumber={contact.primaryPhoneText}
                            email={contact.primaryEmailText}
                            role={contact.jobTitle}
                            actionMenuOptions={[
                                {
                                    label: 'Remove',
                                    icon: <TrashIcon size={12} wrapWithDiv={false} />,
                                    handleSelect: () => handleRemoveContact(contact.id),
                                },
                            ]}
                            tertiaryProps={
                                contact.isNew
                                    ? {
                                          type: 'text-button',
                                          text: 'Edit',
                                          onClick: () => setIsEditingContactId(contact.id),
                                      }
                                    : undefined
                            }
                            icon={
                                <>
                                    {avatarUrlValidated ? (
                                        <Avatar
                                            avatarUrl={avatarUrlValidated}
                                            size={32}
                                            circular={false}
                                            hasBorderRadius={true}
                                        />
                                    ) : (
                                        <IconAvatar
                                            icon={ContactIcon}
                                            avatarSize={32}
                                            iconSize={16}
                                            iconColor={colors.person}
                                            backgroundColor={colors.blueXlt}
                                            shape='square'
                                        />
                                    )}
                                </>
                            }
                        />
                    );
                })}
            {isEditingContactId === NEW_CONTACT_MAGIC_ID && (
                <ContactCardCreateable
                    onSelectContact={handleSelectContact}
                    onSubmit={handleCreateContact}
                    onClose={() => setIsEditingContactId(null)}
                    validate={(contactInput: ContactCreateValidationInput) =>
                        fetchFormValidation(contactInput)
                    }
                    onSearchPotentialContacts={(searchInput: ContactInput) =>
                        getPotentialContacts({
                            contactInput: searchInput,
                            relatedAccountIds: selectedAccountIds,
                            alreadyAddedContactsIds,
                        })
                    }
                    hasRelatedAccounts={Boolean(selectedAccountIds)}
                />
            )}
            {!isEditingContactId && (
                <EntityPageAddItemButton
                    sectionEntity='contacts'
                    parentItemText='a person'
                    onAddItem={() => {
                        setIsEditingContactId(NEW_CONTACT_MAGIC_ID);
                    }}
                    omitEntityName={true}
                />
            )}
        </StackLegacy>
    );
}
